import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { velocityHeatmap } from "@/lib/slotting.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/_authenticated/app/slotting/heatmap")({ component: HeatmapPage });

const RANGES = [7, 30, 90] as const;

type Cell = { id: string; code: string; zone: string; aisle: string; units: number; intensity: number };

function HeatmapPage() {
  const { tenantId, facilityId } = useScope();
  const fn = useServerFn(velocityHeatmap);
  const [days, setDays] = useState<number>(30);

  const { data, isLoading } = useQuery({
    queryKey: ["heatmap", tenantId, facilityId, days],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => fn({ data: { tenantId: tenantId!, facilityId: facilityId!, days } }),
    placeholderData: keepPreviousData,
  });

  const grouped = useMemo(() => {
    const m = new Map<string, Map<string, Cell[]>>();
    for (const c of (data?.cells ?? []) as Cell[]) {
      if (!m.has(c.zone)) m.set(c.zone, new Map());
      const a = m.get(c.zone)!;
      if (!a.has(c.aisle)) a.set(c.aisle, []);
      a.get(c.aisle)!.push(c);
    }
    return m;
  }, [data]);

  return (
    <>
      <PageHeader title="Velocity Heatmap" description="Pick density by location. Brighter = more units picked." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <TabsList>
              {RANGES.map((d) => <TabsTrigger key={d} value={String(d)}>{d}d</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Low</span>
            <div className="h-3 w-32 rounded" style={{ background: "linear-gradient(to right, hsl(var(--muted)), hsl(var(--primary)))" }} />
            <span>High ({data?.max ?? 0})</span>
          </div>
        </div>

        {!facilityId && <Card><CardContent className="p-6 text-sm text-muted-foreground">Select a facility from the top bar.</CardContent></Card>}
        {facilityId && isLoading && <Card><CardContent className="p-6 text-sm text-muted-foreground">Loading…</CardContent></Card>}

        {facilityId && !isLoading && Array.from(grouped.entries()).map(([zone, aisles]) => (
          <Card key={zone}>
            <CardContent className="p-4 space-y-3">
              <div className="text-sm font-semibold">Zone {zone}</div>
              {Array.from(aisles.entries()).map(([aisle, cells]) => (
                <div key={aisle}>
                  <div className="text-xs text-muted-foreground mb-1">Aisle {aisle}</div>
                  <div className="flex flex-wrap gap-1">
                    {cells.map((c) => (
                      <div
                        key={c.id}
                        title={`${c.code} — ${c.units} units`}
                        className="h-7 px-2 rounded text-[10px] font-mono flex items-center justify-center border"
                        style={{
                          background: `color-mix(in oklab, hsl(var(--primary)) ${Math.round(c.intensity * 90)}%, hsl(var(--card)))`,
                          color: c.intensity > 0.5 ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
                        }}
                      >
                        {c.code}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {facilityId && !isLoading && (data?.cells.length ?? 0) === 0 && (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">No locations to display.</CardContent></Card>
        )}
      </div>
    </>
  );
}